import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";

/* -------------------------------------------------------------------------- */
/* AnalyticsModal (com suporte a timezone)                                   */
/* -------------------------------------------------------------------------- */
function AnalyticsModal({ isOpen, onClose, pairNumber, apiToken, fallbackLastResults, selectedTimeZone }) {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // console.log("DEGU", selectedTimeZone)

    // Função para converter timestamp com timezone
    const convertTimeWithTimezone = (timestamp, targetTimezone) => {
        if (!timestamp || !targetTimezone) return "—";

        try {
            let date;
            const tsStr = String(timestamp).trim();

            // Tenta diferentes formatos
            if (tsStr.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
                // Formato: "2025-12-09 18:05:00"
                date = new Date(tsStr.replace(' ', 'T') + 'Z');
            } else if (tsStr.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
                // Formato ISO
                date = new Date(tsStr);
            } else {
                date = new Date(tsStr);
            }

            if (isNaN(date.getTime())) {
                return timestamp;
            }

            // Aplica o offset do timezone
            const offsetHours = targetTimezone.code || 0;
            const offsetMs = offsetHours * 60 * 60 * 1000;
            const adjustedDate = new Date(date.getTime() + offsetMs);

            // Formata a data ajustada
            const year = adjustedDate.getUTCFullYear();
            const month = String(adjustedDate.getUTCMonth() + 1).padStart(2, '0');
            const day = String(adjustedDate.getUTCDate()).padStart(2, '0');
            const hours = String(adjustedDate.getUTCHours()).padStart(2, '0');
            const minutes = String(adjustedDate.getUTCMinutes()).padStart(2, '0');
            const seconds = String(adjustedDate.getUTCSeconds()).padStart(2, '0');

            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;

        } catch (error) {
            // console.error("Erro ao converter horário:", error);
            return String(timestamp);
        }
    };

    // Função para formatar timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "-";
        if (selectedTimeZone) {
            return convertTimeWithTimezone(timestamp, selectedTimeZone);
        } else {
            // Fallback para UTC se não houver timezone selecionado
            try {
                const date = new Date(timestamp);
                if (isNaN(date.getTime())) return timestamp;

                const year = date.getUTCFullYear();
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const day = String(date.getUTCDate()).padStart(2, '0');
                const hours = String(date.getUTCHours()).padStart(2, '0');
                const minutes = String(date.getUTCMinutes()).padStart(2, '0');
                const seconds = String(date.getUTCSeconds()).padStart(2, '0');

                return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
            } catch {
                return timestamp;
            }
        }
    };

    // Atualize os resultados quando o timezone mudar
    useEffect(() => {
        if (!isOpen) {
            setResults([]);
            setError(null);
            setLoading(false);
            return;
        }

        // Se temos fallbackLastResults, converte os timestamps com o timezone atual
        if (fallbackLastResults && fallbackLastResults.length > 0) {
            const updatedResults = fallbackLastResults.map(result => ({
                ...result,
                formattedTimestamp: formatTimestamp(result.Timestamp || result.timestamp)
            }));
            setResults(updatedResults);
            return;
        }

        if (!pairNumber) return;

        const fetchResults = async () => {
            if (!pairNumber || !apiToken) {
                setResults([]);
                return;
            }

            try {
                const resp = await fetch(
                    `${process.env.REACT_APP_BASE_API_URL}/api/predictions/results?asset=${pairNumber}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${apiToken}`,
                        },
                    }
                );

                if (!resp.ok) {
                    throw new Error("Status " + resp.status);
                }

                const data = await resp.json();
                // console.log("DATA RECEBIDA:", data);

                // Aplica formatação com timezone aos resultados
                const formattedData = (Array.isArray(data) ? data : []).map(item => ({
                    ...item,
                    formattedTimestamp: formatTimestamp(item.timestamp || item.Timestamp)
                }));

                setResults(formattedData);
            } catch (err) {
                // console.error(err);
                setError(err.message);
            }
        };

        fetchResults();
    }, [isOpen, pairNumber, apiToken, fallbackLastResults, selectedTimeZone]); // Adicione selectedTimeZone nas dependências

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 p-6 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-auto text-gray-100">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h3 className="text-xl font-bold">Analytics | Last 10 Results</h3>
                        <p className="text-sm text-gray-400">
                            Asset: {pairNumber}
                        </p>
                        <p className="text-sm text-gray-400">
                            Timezone: {selectedTimeZone ? selectedTimeZone?.name : null}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-2xl text-gray-300">×</button>
                </div>

                {error && <div className="text-red-400 mb-4">{error}</div>}
                {loading && <div className="text-gray-400 mb-4">Loading...</div>}

                {!loading && results && results.length > 0 ? (
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="text-gray-400 border-b border-gray-700">
                                <th className="py-2">Timestamp</th>
                                <th className="py-2">Predicted</th>
                                <th className="py-2">Market</th>
                                <th className="py-2">Result</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.slice(0, 50).map((r, i) => {
                                const predicted = r["Predicted Signal"] || r.predicted_signal;
                                const market = r["Market Signal"] || r.market_signal;

                                // Usa o timestamp formatado se existir, caso contrário formata na hora
                                const formatted = r.formattedTimestamp || formatTimestamp(r.timestamp || r.Timestamp);

                                const win = predicted && market && predicted === market;

                                return (
                                    <tr key={i} className="border-b border-gray-800">
                                        <td className="py-2 align-top text-gray-200">{formatted}</td>
                                        <td className="py-2 align-top">{predicted}</td>
                                        <td className="py-2 align-top">{market}</td>
                                        <td className={`py-2 align-top ${win ? "text-green-400" : "text-red-400"}`}>
                                            {win ? "✓ WIN" : "✗ LOSS"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    !loading && <div className="text-gray-400">No results available for this pair.</div>
                )}

                <div className="mt-6 flex justify-end">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-800 rounded text-gray-100">Close</button>
                </div>
            </div>
        </div>
    );
}

export default AnalyticsModal;